import { useEffect, useState } from "react";

import useUserStore from "@store/useUserStore";
import useSystemStore from "@store/useSystemStore";
import usePlatformStore from "@store/usePlatformStore";
import { useTheme } from "@hooks/useTheme";
import { useUserpilot } from "@platform/hooks/useUserpilot";
import { useConnection } from "@hooks/useConnection";
import { useNetworkStatus } from "@hooks/useNetworkStatus";
import { usePlatformItem } from "@hooks/usePlatformItem";
import { useMaintenance } from "@screens/maintenance/hooks/useMaintenance";
import { useMaintenanceAlert } from "@screens/maintenance/hooks/useMaintenanceAlert";
import { ActiveScreenManager, ItemType } from "@enums/common.enum";
import MainLayout from "@components/Layouts/MainLayout";
import MaintenanceScreen from "@screens/maintenance";
import {
  EmailScreenManager,
  MeetingScreenManager,
  ChromeScreenManager,
} from "@components/ScreenManager";
import { AppToaster } from "@ui/AppToaster/AppToaster";
import { TransitionWrapper } from "@ui/Transition";
import { GlobalDialog } from "@ui/Dialogs";
import { AppLoader } from "@ui/Loaders";
import { SideBlade } from "@ui/SideBlade";

const SCREEN_COMPONENTS = {
  [ActiveScreenManager.CHROME]: ChromeScreenManager,
  [ActiveScreenManager.EMAIL]: EmailScreenManager,
  [ActiveScreenManager.MEETING]: MeetingScreenManager,
} as const;

export default function MainScreenManager() {
  const { hasSession } = useUserStore();
  const { itemType } = usePlatformStore();
  const { isAppLoading, isMaintenance } = useSystemStore();
  const [activeScreenManager, setActiveScreenManager] = useState(ActiveScreenManager.CHROME);

  useTheme();
  useUserpilot();
  useConnection();
  usePlatformItem();
  useNetworkStatus();
  const { isChecking: isCheckingMaintenance } = useMaintenance();
  useMaintenanceAlert();

  useEffect(() => {
    if (hasSession && itemType === ItemType.EMAIL) {
      setActiveScreenManager(ActiveScreenManager.EMAIL);
    } else if (hasSession && itemType === ItemType.MEETING) {
      setActiveScreenManager(ActiveScreenManager.MEETING);
    } else {
      setActiveScreenManager(ActiveScreenManager.CHROME);
    }
  }, [hasSession, itemType]);

  const ScreenComponent = SCREEN_COMPONENTS[activeScreenManager];

  if (isMaintenance) {
    return <MaintenanceScreen />;
  }

  // The first status decides between the maintenance page and the normal app, so hold the
  // loader (never a blank pane) until it lands: a backend that is fully down then shows the
  // maintenance page instead of the connection-issue screen.
  if (isCheckingMaintenance) {
    return <AppLoader />;
  }

  return (
    <MainLayout>
      <AppToaster />
      <GlobalDialog />
      <SideBlade />

      <TransitionWrapper screenKey={activeScreenManager} disableTransition={isAppLoading}>
        <ScreenComponent />
      </TransitionWrapper>
    </MainLayout>
  );
}
